import { useEffect, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { AxiosResponse } from 'axios';
import _ from 'lodash';

import { DraggableItemType } from '../../shared/Draggable';
import { DragNDropSpeechBubble } from './DragNDropSpeechBubble';
import {
    DragDropContainer,
    Draggable,
    DraggablePhotos,
    MediaImage,
    SpeechBubble,
} from '../../shared';

import { useMediaProcessingStatus } from '../../hooks/medias';

import {
    ImageFileUploading,
    onSelectPhoto,
    OptionalData,
} from '../../utils/upload';
import { getPackDisplayName } from '../../utils/utils';

import { ImageIconType } from '../../types/enums/imageicontype.enum';
import { MediaType } from '../../types/enums/mediatype.enum';
import { PackType } from '../../types/enums/packtype.enum';

import { Media } from '../../types/mediaTypes';
import { Pack } from '../../types/packTypes';

import wormImg from '../../assets/worm.png';

type PackScreenshotsSectionProps = {
    pack: Pack;
    childPacksData: Pack[];
    items: (Media | (ImageFileUploading & { id?: number }))[];
    onMediasSelected: (
        type: MediaType,
        items: ImageFileUploading[],
        optionalData: OptionalData,
    ) => void;
    onMediasWasUploaded: (
        type: MediaType,
        file: File,
        res: AxiosResponse<{ uploaded: Media }>,
        optionalData: OptionalData,
    ) => void;
    onMediaDelete: (index: number, type: MediaType) => void;
    onMediaDragEnd: (result: {
        source: { index: number; droppableId: MediaType };
        destination: { index: number };
    }) => void;
    toggleScreenshot: (mediaId: number) => void;
    onScreenshotProcessingComplete?: (media: Media) => void;
    onScreenshotProcessingError?: (mediaId: number, message: string) => void;
};

// A tracked screenshot: a server-side Media, optionally enriched with the
// local File while the upload happens in this browser session.
type TrackedScreenshot = Media & { file?: File };

// Renders nothing; polls a single in-progress media and triggers callbacks
// when it finishes or errors. One instance per failing screenshot. Items that
// already carry a terminal processing status (e.g. after a page reload) report
// immediately instead of polling.
const ScreenshotProcessingTracker = ({
    media,
    onProcessingComplete,
    onProcessingError,
}: {
    media: TrackedScreenshot;
    onProcessingComplete: (media: Media) => void;
    onProcessingError: (message: string) => void;
}) => {
    const buildErrorMessage = () => {
        // Freshly uploaded items carry the local File but no server-side
        // originalFileName yet; reloaded items are the opposite.
        const name =
            media.file?.name ?? media.originalFileName ?? `#${media.id}`;
        return `The image "${name}" could not be encoded on the server. Remove it and upload it again as a PNG.`;
    };

    // Failed media stay in the pack (so they can be deleted), which means this
    // tracker keeps rendering for them. Report once per media id.
    const reportedForIdRef = useRef<number | null>(null);
    const reportError = () => {
        if (reportedForIdRef.current === media.id) {
            return;
        }
        reportedForIdRef.current = media.id;
        onProcessingError(buildErrorMessage());
    };

    const { trackMediaProcessing } = useMediaProcessingStatus({
        onProcessingComplete,
        onProcessingError: reportError,
    });

    useEffect(() => {
        if (media.processingStatus === 'error') {
            reportError();
            return;
        }
        trackMediaProcessing(media);
    }, [media, trackMediaProcessing, onProcessingError]);

    return null;
};

// Server-side Media carries a processing status; locally picked files do not.
function hasProcessingStatus(
    item: Media | (ImageFileUploading & { id?: number }),
    status: Media['processingStatus'],
): boolean {
    return 'processingStatus' in item && item.processingStatus === status;
}

function isInProgressProcessing(
    item: Media | (ImageFileUploading & { id?: number }),
): item is Media {
    return hasProcessingStatus(item, 'in_progress');
}

function isFailedProcessing(
    item: Media | (ImageFileUploading & { id?: number }),
): item is Media {
    return hasProcessingStatus(item, 'error');
}

// Data URL for a light-grey rectangle at screenshot aspect ratio; used as an
// <img src> fallback for in-progress screenshots that have no local blob URL
// yet (e.g. after a page reload while the media service is still processing).
const PROCESSING_PLACEHOLDER =
    'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="2208" height="1242"><rect width="100%" height="100%" fill="%23e0e0e0"/></svg>';

export const PackScreenshotsSection = (props: PackScreenshotsSectionProps) => {
    const {
        pack,
        childPacksData,
        items,
        onMediasSelected,
        onMediasWasUploaded,
        onMediaDragEnd,
        onMediaDelete,
        toggleScreenshot,
        onScreenshotProcessingComplete,
        onScreenshotProcessingError,
    } = props;

    const isBundle = pack.type === PackType.MULTIPACK;

    const inProgressItems = items.filter(
        (item) => isInProgressProcessing(item) || isFailedProcessing(item),
    );

    // Reflect background processing state in the thumbnail overlay so the
    // "Uploading"/"Processing" spinner from DraggablePhotos also fires while
    // the media service finishes optipng/resize/S3 upload in the background.
    const itemsWithProcessingOverlay = items.map((item) => {
        if (hasProcessingStatus(item, 'in_progress')) {
            return {
                ...item,
                uploading: true,
                uploadingText: 'Processing',
                url: item.url || PROCESSING_PLACEHOLDER,
            };
        }
        if (hasProcessingStatus(item, 'error')) {
            // Keep failed uploads visible so the user can remove them. The
            // processed asset is unusable, so always show the grey
            // placeholder rather than a possibly stale/broken url.
            return {
                ...item,
                url: PROCESSING_PLACEHOLDER,
                errorText: 'Failed to process',
            };
        }
        return item;
    });

    // Map screenshots with info from child packs media
    const draggableItems: DraggableItemType[] = itemsWithProcessingOverlay.map(
        (parentScreenshot) => {
            let overlayText = pack.displayName.en ?? pack.name;
            let buttonType = ImageIconType.DELETE;

            _.forEach(childPacksData, (pack) => {
                const mediaExistsInTheChild = pack.medias.find(
                    (childMedia) => childMedia.id === parentScreenshot.id,
                );
                if (mediaExistsInTheChild) {
                    overlayText = getPackDisplayName(pack);
                    buttonType = ImageIconType.CHECK;
                    return;
                }
            });

            if (isInProgressProcessing(parentScreenshot)) {
                overlayText = `Processing • ${overlayText}`;
            } else if (isFailedProcessing(parentScreenshot)) {
                overlayText = `Failed to process • ${overlayText}`;
            }

            return {
                // As we put uploading files into screenshots, we need to map media object for render component when it has a file
                media:
                    'file' in parentScreenshot
                        ? {
                              id: parentScreenshot.id,
                              url: parentScreenshot.url,
                          }
                        : parentScreenshot,
                overlayText: overlayText,
                buttonType: buttonType,
                onClick: toggleScreenshot,
            };
        },
    );

    return (
        <>
            <h3>Screenshots (2208×1242)</h3>
            {items.length > 0 ? (
                isBundle ? (
                    <SpeechBubble
                        style={{
                            justifyContent: 'left',
                            marginTop: 10,
                            marginBottom: 20,
                        }}
                    >
                        Rearrange the gallery with drag and drop. You can
                        de-select child pack related media. To add child pack
                        media go to the section above.
                        <br />
                        To upload bundle specific media, simply drag and drop
                        them below.
                    </SpeechBubble>
                ) : (
                    <DragNDropSpeechBubble />
                )
            ) : null}
            <DndProvider backend={HTML5Backend}>
                <DragDropContainer
                    type={MediaType.SCREENSHOT}
                    onMediasSelected={onMediasSelected}
                    onMediasWasUploaded={onMediasWasUploaded}
                >
                    {items.length === 0 && (
                        <div className="noMediaWrapper">
                            <img
                                alt="placeholder no screenshots uploaded"
                                src={wormImg}
                            ></img>
                            <label className="placeholder">
                                No screenshots, just yet. Drag and drop here to
                                upload :)
                            </label>
                        </div>
                    )}
                    {isBundle && (
                        <Draggable
                            items={draggableItems}
                            onDragEnd={onMediaDragEnd}
                            renderer={(item) => <MediaImage {...item} />}
                        />
                    )}
                    {!isBundle && (
                        <DraggablePhotos
                            onDelete={onMediaDelete}
                            type={MediaType.SCREENSHOT}
                            items={itemsWithProcessingOverlay}
                            onDragEnd={onMediaDragEnd}
                        />
                    )}
                </DragDropContainer>
            </DndProvider>
            {onScreenshotProcessingComplete &&
                onScreenshotProcessingError &&
                inProgressItems.map((media) => (
                    <ScreenshotProcessingTracker
                        key={media.id}
                        media={media}
                        onProcessingComplete={onScreenshotProcessingComplete}
                        onProcessingError={(message) =>
                            onScreenshotProcessingError(media.id, message)
                        }
                    />
                ))}
            <div style={{ marginTop: 20 }}>
                <input
                    type="file"
                    multiple={true}
                    name=""
                    id="screenshots"
                    accept="Image/png"
                    onChange={(e) =>
                        onSelectPhoto(
                            e,
                            MediaType.SCREENSHOT,
                            onMediasSelected,
                            onMediasWasUploaded,
                        )
                    }
                />
                <label className="upload" htmlFor="screenshots">
                    Add new
                </label>
            </div>
        </>
    );
};
